import React, { useRef, useState, useEffect } from 'react';
import { convertHeicFileToJpeg, isUnsupportedRawFile } from '../../lib/heicConvert';
import { useLanguage } from '../../context/LanguageContext';
import './CoverEditor.css';

if (!document.getElementById('cover-editor-fonts')) {
  const link = document.createElement('link');
  link.id = 'cover-editor-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Londrina+Solid:wght@300;400;900&family=Playfair+Display:wght@400;700;900&family=Bebas+Neue&family=Oswald:wght@400;700&family=Montserrat:wght@400;500;700;900&family=Anton&family=Abril+Fatface&family=Space+Grotesk:wght@400;700&family=Cormorant+Garamond:wght@400;700&family=Archivo+Black&display=swap';
  document.head.appendChild(link);
}

const FONT_OPTIONS = [
  'Londrina Solid', 'Playfair Display', 'Bebas Neue', 'Oswald',
  'Montserrat', 'Anton', 'Abril Fatface', 'Space Grotesk',
  'Cormorant Garamond', 'Archivo Black'
];

export const DEFAULT_COVER_DATA = {
  activeSide: 'front',
  style: {
    bg: '#ffc2ec',
    titleColor: '#ffffff',
    yearColor: '#ffffff',
    urlColor: '#ffffff',
    titleSize: 116,
    yearSize: 40,
    fontFamily: 'Londrina Solid',
    titleX: 50,
    titleY: 8,
    yearX: 50,
    yearY: 24,
    spineTextColor: '#5c2828',
  },
  meta: {
    title: 'PARIS',
    year: new Date().getFullYear().toString(),
    spineTitle: 'Vendi',
    spineDate: 'Data',
  },
  frontLayout: 'none',
  backLayout: 'grid-4',
  frontSlots: [],
  backSlots: [null, null, null, null],
  frontStickers: [{
    id: 'default-front-sticker',
    svgUrl: 'https://dpvgqakywrbgzripshjy.supabase.co/storage/v1/object/public/sticker-svgs/stickers/2/1788605660742_m2k9k0.svg',
    x: 22, y: 31, width: 56, height: 66,
  }],
  backStickers: [],
  spineSticker: null,
};

const API_URL = process.env.REACT_APP_API_URL || 'https://orders.fletaime.com';

function readFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = e => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ── Draggable text overlay ─────────────────────────────────────────────────
// A plain click/tap (no movement) enters inline editing instead of requiring
// the customer to open the side panel and find the matching text field.
const DRAG_THRESHOLD_PX = 3;

const DraggableText = ({ value, x, y, textStyle, onMove, onChange }) => {
  const elRef = useRef(null);
  const inputRef = useRef(null);
  const draggedRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  useEffect(() => {
    if (editing) { inputRef.current?.focus(); inputRef.current?.select(); }
  }, [editing]);

  const startDrag = (clientX, clientY) => {
    const page = elRef.current?.closest('.cover-page');
    if (!page) return;
    const rect = page.getBoundingClientRect();
    const ox = x, oy = y, mx = clientX, my = clientY;
    draggedRef.current = false;

    const onMouseMove = e => {
      if (Math.abs(e.clientX - mx) > DRAG_THRESHOLD_PX || Math.abs(e.clientY - my) > DRAG_THRESHOLD_PX) {
        draggedRef.current = true;
      }
      onMove(
        Math.max(2, Math.min(96, ox + ((e.clientX - mx) / rect.width) * 100)),
        Math.max(2, Math.min(96, oy + ((e.clientY - my) / rect.height) * 100))
      );
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (!draggedRef.current && onChange) setEditing(true);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const startTouch = e => {
    const t = e.touches[0];
    const page = elRef.current?.closest('.cover-page');
    if (!page) return;
    const rect = page.getBoundingClientRect();
    const ox = x, oy = y, mx = t.clientX, my = t.clientY;
    draggedRef.current = false;

    const onTouchMove = te => {
      te.preventDefault();
      const tt = te.touches[0];
      if (Math.abs(tt.clientX - mx) > DRAG_THRESHOLD_PX || Math.abs(tt.clientY - my) > DRAG_THRESHOLD_PX) {
        draggedRef.current = true;
      }
      onMove(
        Math.max(2, Math.min(96, ox + ((tt.clientX - mx) / rect.width) * 100)),
        Math.max(2, Math.min(96, oy + ((tt.clientY - my) / rect.height) * 100))
      );
    };
    const onTouchEnd = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      if (!draggedRef.current && onChange) setEditing(true);
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  };

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (onChange && trimmed && trimmed !== value) onChange(trimmed);
    else setDraft(value);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="cover-drag-text cover-drag-text-input"
        style={{
          position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translateX(-50%)',
          ...textStyle, width: `${Math.max(3, draft.length + 1)}ch`, textAlign: 'center',
        }}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); inputRef.current.blur(); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        onMouseDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
      />
    );
  }

  return (
    <div
      ref={elRef}
      className="cover-drag-text"
      style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translateX(-50%)', ...textStyle }}
      onMouseDown={e => { e.stopPropagation(); startDrag(e.clientX, e.clientY); }}
      onTouchStart={e => { e.stopPropagation(); startTouch(e); }}
    >
      {value}
    </div>
  );
};

// ── Draggable + resizable sticker overlay ──────────────────────────────────
const CoverSticker = ({ sticker, onMove, onResize, onRemove }) => {
  const { t } = useLanguage();
  const elRef = useRef(null);
  const { x, y, width, height, svgUrl } = sticker;

  // Mobile Safari has occasionally failed to paint this <img> on first
  // load of a fresh session (fixed itself only after a manual page
  // refresh) - most likely a transient network/DNS hiccup on the first
  // request to the Supabase Storage CDN. Retry a couple of times with a
  // cache-busting query param instead of leaving it permanently blank.
  const [retrySrc, setRetrySrc] = useState(svgUrl);
  const retryCountRef = useRef(0);
  useEffect(() => { retryCountRef.current = 0; setRetrySrc(svgUrl); }, [svgUrl]);
  const handleImgError = () => {
    if (retryCountRef.current >= 3) return;
    retryCountRef.current += 1;
    setTimeout(() => {
      setRetrySrc(`${svgUrl}${svgUrl.includes('?') ? '&' : '?'}retry=${retryCountRef.current}_${Date.now()}`);
    }, 400 * retryCountRef.current);
  };

  const startDrag = (clientX, clientY) => {
    const page = elRef.current?.closest('.cover-page');
    if (!page) return;
    const rect = page.getBoundingClientRect();
    const ox = x, oy = y, mx = clientX, my = clientY;

    const onMouseMove = e => {
      const nx = Math.max(0, Math.min(100 - width, ox + ((e.clientX - mx) / rect.width) * 100));
      const ny = Math.max(0, Math.min(100 - height, oy + ((e.clientY - my) / rect.height) * 100));
      onMove(nx, ny);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const startTouch = e => {
    const t0 = e.touches[0];
    const page = elRef.current?.closest('.cover-page');
    if (!page) return;
    const rect = page.getBoundingClientRect();
    const ox = x, oy = y, mx = t0.clientX, my = t0.clientY;

    const onTouchMove = te => {
      te.preventDefault();
      const tt = te.touches[0];
      const nx = Math.max(0, Math.min(100 - width, ox + ((tt.clientX - mx) / rect.width) * 100));
      const ny = Math.max(0, Math.min(100 - height, oy + ((tt.clientY - my) / rect.height) * 100));
      onMove(nx, ny);
    };
    const onTouchEnd = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  };

  // Aspect-locked resize from any of the 4 corners - each keeps the
  // OPPOSITE corner fixed (br keeps top-left fixed, tl keeps bottom-right
  // fixed, tr keeps bottom-left fixed, bl keeps top-right fixed). Scale
  // factor is computed in PIXEL space (via the measured page rect) then
  // converted back to percent per-axis - .cover-page isn't square, so
  // reusing a single percent-space ratio here would visibly distort the
  // sticker. newX/newY are always returned (echoing the unchanged value
  // on whichever axis this corner doesn't move) so the caller can apply
  // them uniformly regardless of which corner was dragged.
  const computeResize = (anchor, dxPx, dyPx, rect) => {
    const owPx = (width / 100) * rect.width;
    const ohPx = (height / 100) * rect.height;
    const signX = (anchor === 'tl' || anchor === 'bl') ? -1 : 1;
    const signY = (anchor === 'tl' || anchor === 'tr') ? -1 : 1;
    const scaleX = (owPx + signX * dxPx) / owPx;
    const scaleY = (ohPx + signY * dyPx) / ohPx;
    const scale = Math.max(0.1, (scaleX + scaleY) / 2);
    const newWPx = Math.max(24, owPx * scale);
    const newHPx = Math.max(24, ohPx * scale);
    const newWidth = (newWPx / rect.width) * 100;
    const newHeight = (newHPx / rect.height) * 100;
    const newX = signX === -1 ? Math.max(0, (x + width) - newWidth) : x;
    const newY = signY === -1 ? Math.max(0, (y + height) - newHeight) : y;
    return { newWidth, newHeight, newX, newY };
  };

  const startResize = (anchor, clientX, clientY) => {
    const page = elRef.current?.closest('.cover-page');
    if (!page) return;
    const rect = page.getBoundingClientRect();
    const mx = clientX, my = clientY;

    const onMouseMove = e => {
      const { newWidth, newHeight, newX, newY } = computeResize(anchor, e.clientX - mx, e.clientY - my, rect);
      onResize(newWidth, newHeight, newX, newY);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const startResizeTouch = (anchor, e) => {
    const t0 = e.touches[0];
    const page = elRef.current?.closest('.cover-page');
    if (!page) return;
    const rect = page.getBoundingClientRect();
    const mx = t0.clientX, my = t0.clientY;

    const onTouchMove = te => {
      te.preventDefault();
      const tt = te.touches[0];
      const { newWidth, newHeight, newX, newY } = computeResize(anchor, tt.clientX - mx, tt.clientY - my, rect);
      onResize(newWidth, newHeight, newX, newY);
    };
    const onTouchEnd = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  };

  return (
    <div
      ref={elRef}
      className="cover-sticker"
      style={{ left: `${x}%`, top: `${y}%`, width: `${width}%`, height: `${height}%` }}
      onMouseDown={e => { e.stopPropagation(); startDrag(e.clientX, e.clientY); }}
      onTouchStart={e => { e.stopPropagation(); startTouch(e); }}
    >
      <img src={retrySrc} alt="" draggable={false} loading="eager" decoding="sync" onError={handleImgError} />
      <button
        className="csb csb-del cover-sticker-del"
        onClick={e => { e.stopPropagation(); onRemove(); }}
        title={t('cover.delete')}
      >×</button>
      <div
        className="cover-sticker-handle cover-sticker-handle-tl"
        onMouseDown={e => { e.stopPropagation(); startResize('tl', e.clientX, e.clientY); }}
        onTouchStart={e => { e.stopPropagation(); startResizeTouch('tl', e); }}
      />
      <div
        className="cover-sticker-handle cover-sticker-handle-tr"
        onMouseDown={e => { e.stopPropagation(); startResize('tr', e.clientX, e.clientY); }}
        onTouchStart={e => { e.stopPropagation(); startResizeTouch('tr', e); }}
      />
      <div
        className="cover-sticker-handle cover-sticker-handle-bl"
        onMouseDown={e => { e.stopPropagation(); startResize('bl', e.clientX, e.clientY); }}
        onTouchStart={e => { e.stopPropagation(); startResizeTouch('bl', e); }}
      />
      <div
        className="cover-sticker-handle cover-sticker-handle-br"
        onMouseDown={e => { e.stopPropagation(); startResize('br', e.clientX, e.clientY); }}
        onTouchStart={e => { e.stopPropagation(); startResizeTouch('br', e); }}
      />
    </div>
  );
};

// ── External bar (rendered ABOVE the cover page, not inside) ─────────────
const SlotExternalBar = ({ slot, onUpdate, onRemove, onUpload }) => {
  const { t } = useLanguage();
  const hasImage = Boolean(slot);
  const zoom     = slot?.zoom     || 1;
  const rotation = slot?.rotation || 0;
  return (
    <div className="csb-bar csb-bar-external">
      <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={e => { e.stopPropagation(); if (hasImage) onUpdate({ ...slot, zoom: Math.max(1, Math.min(3.5, zoom + 0.15)) }); }} title={t('cover.zoomIn')}>+</button>
      <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={e => { e.stopPropagation(); if (hasImage) onUpdate({ ...slot, zoom: Math.max(1, Math.min(3.5, zoom - 0.15)) }); }} title={t('cover.zoomOut')}>−</button>
      <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={e => { e.stopPropagation(); if (hasImage) onUpdate({ ...slot, rotation: (rotation + 90) % 360, panX: 0, panY: 0 }); }} title={t('cover.rotate')}>↺</button>
      <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={e => { e.stopPropagation(); if (hasImage) onUpdate({ ...slot, zoom: 1, panX: 0, panY: 0, rotation: 0 }); }} title={t('cover.reset')}>R</button>
      <button className="csb csb-del" onClick={e => { e.stopPropagation(); onRemove(); }} title={t('cover.delete')}>×</button>
    </div>
  );
};

// ── Single photo slot with controls (+, -, ↺, R, ×) ──────────────────────
const CoverPhotoSlot = ({ slot, onUpdate, onRemove, onUpload, mini = false, noBar = false }) => {
  const { t } = useLanguage();
  const hasImage = Boolean(slot);
  const zoom     = slot?.zoom     || 1;
  const panX     = slot?.panX     || 0;
  const panY     = slot?.panY     || 0;
  const rotation = slot?.rotation || 0;
  const contentRef = useRef(null);

  // The pan is stored in CSS px of the slot as displayed right now; the
  // server needs that slot size to scale it to the printed cover, otherwise
  // it can't reproduce what the customer framed (see renderCoverSlot).
  const slotSize = () => {
    const r = contentRef.current?.getBoundingClientRect();
    return r && r.width > 0 ? { panSlotW: r.width, panSlotH: r.height } : {};
  };

  const doZoom = (e, d) => {
    e.stopPropagation();
    if (!hasImage) return;
    onUpdate({ ...slot, zoom: Math.max(1, Math.min(3.5, zoom + d)), ...slotSize() });
  };
  const doRotate = e => {
    e.stopPropagation();
    if (!hasImage) return;
    onUpdate({ ...slot, rotation: (rotation + 90) % 360, panX: 0, panY: 0, ...slotSize() });
  };
  const doReset = e => {
    e.stopPropagation();
    if (!hasImage) return;
    onUpdate({ ...slot, zoom: 1, panX: 0, panY: 0, rotation: 0 });
  };

  const startPan = (cx, cy) => {
    if (!hasImage) return;
    const ox = panX, oy = panY;
    const onMove = e => onUpdate({ ...slot, panX: ox + (e.clientX - cx), panY: oy + (e.clientY - cy), ...slotSize() });
    const onUp   = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const startTouch = e => {
    if (!hasImage) return;
    const t = e.touches[0];
    const ox = panX, oy = panY, mx = t.clientX, my = t.clientY;
    const onMove = te => { te.preventDefault(); const tt = te.touches[0]; onUpdate({ ...slot, panX: ox + (tt.clientX - mx), panY: oy + (tt.clientY - my), ...slotSize() }); };
    const onEnd  = () => { document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onEnd); };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  return (
    <div className={`csw${mini ? ' csw-mini' : ''}`}>
      {/* Bar brenda — vetëm për mini slots (grid-4) */}
      {!noBar && (
        <div className={`csb-bar${mini ? ' csb-bar-mini-del' : ''}`}>
          {!mini && (
            <>
              <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={e => doZoom(e,  0.15)} title={t('cover.zoomIn')}>+</button>
              <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={e => doZoom(e, -0.15)} title={t('cover.zoomOut')}>−</button>
              <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={doRotate}              title={t('cover.rotate')}>↺</button>
              <button className={`csb${!hasImage ? ' csb-dis' : ''}`} onClick={doReset}               title={t('cover.reset')}>R</button>
            </>
          )}
          <button className="csb csb-del" onClick={e => { e.stopPropagation(); onRemove(); }} title={t('cover.delete')}>×</button>
        </div>
      )}

      {/* Content area — mini slots are fixed (no pan/drag) */}
      <div
        ref={contentRef}
        className={`csw-content${hasImage ? '' : ' csw-empty'}`}
        onClick={() => !hasImage && onUpload()}
        onMouseDown={hasImage && !mini ? e => { e.preventDefault(); startPan(e.clientX, e.clientY); } : undefined}
        onTouchStart={hasImage && !mini ? e => { e.stopPropagation(); startTouch(e); } : undefined}
        style={{ cursor: hasImage && !mini ? 'grab' : 'pointer' }}
      >
        {hasImage ? (
          <img
            src={slot.src} alt="" className="csw-img" draggable={false}
            style={mini ? undefined : { transform: `translate(${panX}px,${panY}px) rotate(${rotation}deg) scale(${zoom})` }}
          />
        ) : (
          <div className="csw-placeholder">
            <span>+</span>
            <span>{t('cover.uploadPhoto')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Photo area wrapper ────────────────────────────────────────────────────
const PhotoArea = ({ layout, slots, onSlotClick, onSlotUpdate, onSlotRemove, noBar = false }) => {
  if (!layout || layout === 'none') return null;
  if (layout === 'grid-4') {
    return (
      <div className="cover-photo-area grid-4">
        {[0, 1, 2, 3].map(i => (
          <CoverPhotoSlot
            key={i}
            slot={slots[i]}
            mini
            onUpload={() => onSlotClick(i)}
            onUpdate={s => onSlotUpdate(i, s)}
            onRemove={() => onSlotRemove(i)}
          />
        ))}
      </div>
    );
  }
  if (layout === 'full-bleed') {
    return (
      <div className="cover-photo-area full-bleed">
        <CoverPhotoSlot
          slot={slots[0]}
          noBar={noBar}
          onUpload={() => onSlotClick(0)}
          onUpdate={s => onSlotUpdate(0, s)}
          onRemove={() => onSlotRemove(0)}
        />
      </div>
    );
  }
  return (
    <div className="cover-photo-area full">
      <CoverPhotoSlot
        slot={slots[0]}
        noBar={noBar}
        onUpload={() => onSlotClick(0)}
        onUpdate={s => onSlotUpdate(0, s)}
        onRemove={() => onSlotRemove(0)}
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// EXPORTED: CoverControls — the left sidebar controls panel
// ══════════════════════════════════════════════════════════════════
export const CoverControls = ({ coverData, onUpdate, onCloseDrawer }) => {
  const { t } = useLanguage();
  const cover = coverData || DEFAULT_COVER_DATA;
  const activeSide = cover.activeSide || 'front';
  const activeLayout = activeSide === 'front' ? cover.frontLayout : cover.backLayout;

  const update      = ch => onUpdate({ ...cover, ...ch });
  const updateStyle = ch => onUpdate({ ...cover, style: { ...cover.style, ...ch } });
  const updateMeta  = ch => onUpdate({ ...cover, meta:  { ...cover.meta,  ...ch } });

  // ── Stickers panel state ──────────────────────────────────────────────
  const [stickerCategories, setStickerCategories] = useState([]);
  const [activeStickerCat, setActiveStickerCat] = useState(null);
  const [categoryStickers, setCategoryStickers] = useState([]);
  const [loadingStickers, setLoadingStickers] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/sticker-categories`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setStickerCategories(list);
        if (list.length > 0) setActiveStickerCat(list[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeStickerCat) { setCategoryStickers([]); return; }
    setLoadingStickers(true);
    fetch(`${API_URL}/api/sticker-categories/${activeStickerCat}/stickers`)
      .then(r => r.json())
      .then(data => { setCategoryStickers(Array.isArray(data) ? data : []); setLoadingStickers(false); })
      .catch(() => setLoadingStickers(false));
  }, [activeStickerCat]);

  const addSticker = (svgUrl) => {
    // The spine's "+" button (CoverPreview) sets `activeStickerTarget:
    // 'spine'` before switching to this tab, so a sticker picked right
    // after lands on the spine instead of front/back. It's a one-shot
    // target, not a persistent mode - see the rail's onClick below, which
    // clears it on any manual tab click.
    if (cover.activeStickerTarget === 'spine') {
      update({ spineSticker: { svgUrl } });
      // On mobile this panel lives inside the full-screen drawer, which
      // hides the canvas underneath - close it so the customer immediately
      // sees the sticker land, instead of it looking like nothing happened.
      // No-op on desktop (there's no drawer to close there).
      onCloseDrawer?.();
      return;
    }
    const key = activeSide === 'front' ? 'frontStickers' : 'backStickers';
    const place = (width, height) => {
      const newSticker = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        svgUrl, width, height, x: (100 - width) / 2, y: (100 - height) / 2,
      };
      update({ [key]: [...(cover[key] || []), newSticker] });
      onCloseDrawer?.();
    };
    // Size the default box to the sticker's own aspect ratio (converted
    // through .cover-page.large's fixed 33/48 aspect-ratio, so width%/
    // height% render visually undistorted from the start) instead of a
    // plain 30x30 square - otherwise a wide/tall sticker would be stuck
    // letterboxed inside a mismatched box forever, since resize always
    // preserves whichever box aspect it started with.
    const img = new window.Image();
    img.onload = () => {
      const w = img.naturalWidth, h = img.naturalHeight;
      if (w > 0 && h > 0) {
        const width = 30;
        const height = Math.max(5, Math.min(90, (width * (33 / 48)) / (w / h)));
        place(width, height);
      } else {
        place(30, 30);
      }
    };
    img.onerror = () => place(30, 30);
    img.src = svgUrl;
  };

  const setLayout = (side, layout) => {
    const count = layout === 'grid-4' ? 4 : layout === 'none' ? 0 : 1;
    const slots = new Array(count).fill(null);
    side === 'front'
      ? update({ frontLayout: layout, frontSlots: slots })
      : update({ backLayout: layout, backSlots: slots });
  };

  // ── Rail tabs: one panel open at a time (Pixory-style), instead of all
  // three sections stacked and scrolled at once. ──────────────────────────
  const [activeTab, setActiveTab] = useState('text');
  const TABS = [
    { id: 'text', label: t('cover.tabText'), icon: 'Aa' },
    { id: 'layout', label: t('cover.tabLayout'), icon: '▦' },
    { id: 'stickers', label: t('cover.tabStickers'), icon: '✦' },
  ];

  // The spine's "+" button (CoverPreview) requests this panel to jump to
  // the Stickers tab by bumping `_openStickersTab` (a plain timestamp, not
  // itself meaningful) on the shared coverData - both components already
  // receive the same coverData/onUpdate, so no extra prop plumbing is
  // needed through PhotoBookDesigner.js/PagesManager.js.
  useEffect(() => {
    if (cover._openStickersTab) setActiveTab('stickers');
  }, [cover._openStickersTab]);

  return (
    <div className="cover-controls-panel cover-controls-tabbed">
      <div className="cover-rail">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`cover-rail-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.id); if (cover.activeStickerTarget) update({ activeStickerTarget: null }); }}
          >
            <span className="cover-rail-icon">{tab.icon}</span>
            <span className="cover-rail-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="cover-panel-body">
      {activeTab === 'text' && (<>

        <div className="cover-field">
          <label>{t('cover.background')}</label>
          <div className="cover-color-row">
            <input type="color" value={cover.style.bg}
              onChange={e => updateStyle({ bg: e.target.value })} />
            <span className="cover-color-hex">{cover.style.bg}</span>
          </div>
        </div>
        <div className="cover-field">
          <label>{t('cover.titleColor')}</label>
          <div className="cover-color-row">
            <input type="color" value={cover.style.titleColor}
              onChange={e => updateStyle({ titleColor: e.target.value })} />
            <span className="cover-color-hex">{cover.style.titleColor}</span>
          </div>
        </div>
        <div className="cover-field">
          <label>{t('cover.yearColor')}</label>
          <div className="cover-color-row">
            <input type="color" value={cover.style.yearColor}
              onChange={e => updateStyle({ yearColor: e.target.value })} />
            <span className="cover-color-hex">{cover.style.yearColor}</span>
          </div>
        </div>
        <div className="cover-field">
          <label>{t('cover.urlColor')}</label>
          <div className="cover-color-row">
            <input type="color" value={cover.style.urlColor || '#ef6d49'}
              onChange={e => updateStyle({ urlColor: e.target.value })} />
            <span className="cover-color-hex">{cover.style.urlColor || '#ef6d49'}</span>
          </div>
        </div>
        <div className="cover-field">
          <label>{t('cover.font')}</label>
          <select value={cover.style.fontFamily}
            onChange={e => updateStyle({ fontFamily: e.target.value })}>
            {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="cover-field">
          <label>{t('cover.titleSize')} <strong>{cover.style.titleSize}</strong></label>
          <input type="range" min="20" max="160" value={cover.style.titleSize}
            onChange={e => updateStyle({ titleSize: parseInt(e.target.value) })} />
        </div>
        <div className="cover-field">
          <label>{t('cover.yearSize')} <strong>{cover.style.yearSize}</strong></label>
          <input type="range" min="16" max="120" value={cover.style.yearSize}
            onChange={e => updateStyle({ yearSize: parseInt(e.target.value) })} />
        </div>
        <div className="cover-field">
          <label>{t('cover.titleLabel')}</label>
          <input type="text" value={cover.meta.title}
            onChange={e => updateMeta({ title: e.target.value })}
            placeholder={t('cover.titlePlaceholder')} />
        </div>
        <div className="cover-field">
          <label>{t('cover.yearLabel')}</label>
          <input type="text" value={cover.meta.year}
            onChange={e => updateMeta({ year: e.target.value })}
            placeholder="2025" />
        </div>
        <div className="cover-field">
          <label>{t('cover.spineTitleLabel')}</label>
          <input type="text" value={cover.meta.spineTitle || ''}
            onChange={e => updateMeta({ spineTitle: e.target.value })}
            placeholder={t('cover.spineTitlePlaceholder')} />
        </div>
        <div className="cover-field">
          <label>{t('cover.spineDateLabel')}</label>
          <input type="text" value={cover.meta.spineDate || ''}
            onChange={e => updateMeta({ spineDate: e.target.value })}
            placeholder={t('cover.spineDatePlaceholder')} />
        </div>
        <div className="cover-field">
          <label>{t('cover.spineColor')}</label>
          <div className="cover-color-row">
            <input type="color" value={cover.style.spineTextColor || '#5c2828'}
              onChange={e => updateStyle({ spineTextColor: e.target.value })} />
            <span className="cover-color-hex">{cover.style.spineTextColor || '#5c2828'}</span>
          </div>
        </div>
        <p className="cover-spine-note">{t('cover.spineNote')}</p>
      </>)}

      {activeTab === 'layout' && (<>
        <div className="cover-side-tabs">
          <button className={`cover-side-tab${activeSide === 'back' ? ' active' : ''}`}
            onClick={() => update({ activeSide: 'back' })} type="button">{t('cover.back')}</button>
          <button className={`cover-side-tab${activeSide === 'front' ? ' active' : ''}`}
            onClick={() => update({ activeSide: 'front' })} type="button">{t('cover.front')}</button>
        </div>
        <div className="cover-layout-btns">
          <button className={`cover-layout-btn${activeLayout === 'full' ? ' active' : ''}`}
            onClick={() => setLayout(activeSide, 'full')} type="button">
            <div className="layout-icon-box"><div className="layout-icon-preview full" /></div>
            <span>{t('cover.layout1Photo')}</span>
          </button>
          <button className={`cover-layout-btn${activeLayout === 'grid-4' ? ' active' : ''}`}
            onClick={() => setLayout(activeSide, 'grid-4')} type="button">
            <div className="layout-icon-box"><div className="layout-icon-preview grid" /></div>
            <span>{t('cover.layout4Photo')}</span>
          </button>
          <button className={`cover-layout-btn${activeLayout === 'full-bleed' ? ' active' : ''}`}
            onClick={() => setLayout(activeSide, 'full-bleed')} type="button">
            <div className="layout-icon-box"><div className="layout-icon-preview full-bleed" /></div>
            <span>{t('cover.layoutFullPage')}</span>
          </button>
          <button className={`cover-layout-btn${activeLayout === 'none' ? ' active' : ''}`}
            onClick={() => setLayout(activeSide, 'none')} type="button">
            <div className="layout-icon-box"><div className="layout-icon-preview none-layout" /></div>
            <span>{t('cover.layoutNoPhoto')}</span>
          </button>
        </div>
      </>)}

      {activeTab === 'stickers' && (<>
        {stickerCategories.length > 0 && (
          <div className="cover-sticker-cats">
            {stickerCategories.map(cat => (
              <button
                key={cat.id}
                type="button"
                className={`cover-sticker-cat${activeStickerCat === cat.id ? ' active' : ''}`}
                onClick={() => setActiveStickerCat(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
        {loadingStickers ? (
          <p className="cover-spine-note">{t('cover.stickersLoading')}</p>
        ) : categoryStickers.length === 0 ? (
          <p className="cover-spine-note">{t('cover.stickersEmpty')}</p>
        ) : (
          <div className="cover-stickers-grid">
            {categoryStickers.map(s => (
              <button
                key={s.id}
                type="button"
                className="cover-sticker-thumb"
                onClick={() => addSticker(s.svg_url)}
                title={s.name || ''}
              >
                <img src={s.svg_url} alt={s.name || ''} draggable={false} />
              </button>
            ))}
          </div>
        )}
      </>)}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// EXPORTED: CoverPreview — the stage preview (with file handling)
// ══════════════════════════════════════════════════════════════════
export const CoverPreview = ({ coverData, onUpdate, onOpenDrawer }) => {
  const { t } = useLanguage();
  const slotFileRef = useRef(null);
  const pendingRef  = useRef(null);
  const spreadRef   = useRef(null);
  const frontPageRef = useRef(null);

  // Scale fonts proportionally to actual rendered cover width.
  // 440px is the reference width where slider values (72/40) look correct.
  const [coverWidth, setCoverWidth] = useState(440);
  useEffect(() => {
    const el = spreadRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (w > 0) setCoverWidth(w / 2);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const sf = Math.min(1, coverWidth / 440);
  // Sized against the spine's own computed width (`Math.max(10,
  // Math.round(18 * sf))` below) minus a hair for the spine's inset
  // border-shadow, so the icon fits without being clipped by
  // `.cover-spine`'s `overflow: hidden`.
  const spineIconPx = Math.max(8, Math.round(18 * sf) - 2);

  // Faqja (.cover-page) fillon pak më poshtë se maja e .cover-spread-large,
  // sepse sipër saj ka rreshtin e bar-it të jashtëm (i fshehur me
  // visibility:hidden kur s'aplikohet, por që zë vend prapë) — dhe në
  // mobil lartësia e faqes bëhet "auto" (aspect-ratio) në vend të një
  // vlere fikse. I matim të dyja REALISHT (jo me vlera "me sy" as për
  // desktop as për mobil) që shpina të përputhet saktë me faqet në çdo
  // madhësi ekrani.
  const [spineTopOffset, setSpineTopOffset] = useState(0);
  const [spineHeight, setSpineHeight] = useState(0);
  useEffect(() => {
    const spread = spreadRef.current;
    const page = frontPageRef.current;
    if (!spread || !page) return;
    const measure = () => {
      const pageRect = page.getBoundingClientRect();
      const spreadRect = spread.getBoundingClientRect();
      setSpineTopOffset(pageRect.top - spreadRect.top);
      setSpineHeight(pageRect.height);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(spread);
    return () => ro.disconnect();
  }, []);

  // Popup i shkurtër udhëzues — shfaqet një herë (për shfletues) kur klienti
  // hyn te redaktimi i kopertinës, që t'i tregojë se çka mund të ndryshojë,
  // përfshi shiritin e ri anash (shpina).
  const INTRO_SEEN_KEY = 'fletaime_cover_intro_seen';
  const [showIntro, setShowIntro] = useState(() => {
    try { return !localStorage.getItem(INTRO_SEEN_KEY); } catch (e) { return true; }
  });
  const dismissIntro = () => {
    setShowIntro(false);
    try { localStorage.setItem(INTRO_SEEN_KEY, '1'); } catch (e) { /* noop */ }
  };

  const cover = coverData || DEFAULT_COVER_DATA;

  const update      = ch => onUpdate({ ...cover, ...ch });
  const updateStyle = ch => onUpdate({ ...cover, style: { ...cover.style, ...ch } });
  const updateMeta  = ch => onUpdate({ ...cover, meta: { ...cover.meta, ...ch } });

  const handleSlotClick = (side, index) => {
    pendingRef.current = { side, index };
    slotFileRef.current.value = '';
    slotFileRef.current.click();
  };

  const makeAsset = async f => {
    const converted = await convertHeicFileToJpeg(f);
    return {
      id: Date.now() + Math.random(), src: await readFile(converted), name: converted.name,
      zoom: 1, panX: 0, panY: 0, rotation: 0
    };
  };

  const handleSlotFiles = async e => {
    const files = Array.from(e.target.files || []);
    if (files[0] && isUnsupportedRawFile(files[0])) {
      alert(t('product.alert.rawFormat', { name: files[0].name }));
      e.target.value = '';
      return;
    }
    const pending = pendingRef.current;
    if (!files.length || !pending) return;
    pendingRef.current = null;
    const { side, index } = pending;
    const asset = await makeAsset(files[0]);
    const prev = side === 'front' ? [...(cover.frontSlots || [])] : [...(cover.backSlots || [])];
    prev[index] = asset;
    side === 'front' ? update({ frontSlots: prev }) : update({ backSlots: prev });
    e.target.value = '';
  };

  const updateSlot = (side, index, newSlot) => {
    const prev = side === 'front' ? [...(cover.frontSlots || [])] : [...(cover.backSlots || [])];
    prev[index] = newSlot;
    side === 'front' ? update({ frontSlots: prev }) : update({ backSlots: prev });
  };

  const removeSlot = (side, index) => {
    const prev = side === 'front' ? [...(cover.frontSlots || [])] : [...(cover.backSlots || [])];
    prev[index] = null;
    side === 'front' ? update({ frontSlots: prev }) : update({ backSlots: prev });
  };

  const updateSticker = (side, id, changes) => {
    const key = side === 'front' ? 'frontStickers' : 'backStickers';
    const next = (cover[key] || []).map(s => s.id === id ? { ...s, ...changes } : s);
    update({ [key]: next });
  };

  const removeSticker = (side, id) => {
    const key = side === 'front' ? 'frontStickers' : 'backStickers';
    update({ [key]: (cover[key] || []).filter(s => s.id !== id) });
  };

  const titleStyle = {
    color: cover.style.titleColor,
    fontSize: `${Math.round(cover.style.titleSize * sf)}px`,
    fontFamily: `'${cover.style.fontFamily}', sans-serif`,
    fontWeight: 900, letterSpacing: '0.04em',
    textShadow: '0 1px 6px rgba(0,0,0,0.15)', lineHeight: 1, whiteSpace: 'nowrap',
  };
  const yearStyle = {
    color: cover.style.yearColor,
    fontSize: `${Math.round(cover.style.yearSize * sf)}px`,
    fontFamily: `'${cover.style.fontFamily}', sans-serif`,
    letterSpacing: '0.06em', lineHeight: 1, whiteSpace: 'nowrap',
  };

  const backHasExternalBar = cover.backLayout === 'full' || cover.backLayout === 'full-bleed';
  const frontHasExternalBar = cover.frontLayout === 'full' || cover.frontLayout === 'full-bleed';

  return (
    <div className="cover-preview-stage">
      <div className="cover-spread-large" ref={spreadRef}>

        {/* ── Back cover ── */}
        <div className="cover-page-wrap">
          <div style={{ visibility: backHasExternalBar ? 'visible' : 'hidden', pointerEvents: backHasExternalBar ? 'auto' : 'none' }}>
            <SlotExternalBar
              slot={(cover.backSlots || [])[0]}
              onUpdate={s => updateSlot('back', 0, s)}
              onRemove={() => removeSlot('back', 0)}
              onUpload={() => handleSlotClick('back', 0)}
            />
          </div>
          <div className="cover-page large cover-back" style={{ backgroundColor: cover.style.bg }}>
            <PhotoArea
              layout={cover.backLayout}
              slots={cover.backSlots || [null]}
              onSlotClick={i => handleSlotClick('back', i)}
              onSlotUpdate={(i, s) => updateSlot('back', i, s)}
              onSlotRemove={i => removeSlot('back', i)}
              noBar={backHasExternalBar}
            />
            {(cover.backStickers || []).map(s => (
              <CoverSticker
                key={s.id}
                sticker={s}
                onMove={(nx, ny) => updateSticker('back', s.id, { x: nx, y: ny })}
                onResize={(nw, nh, nx, ny) => updateSticker('back', s.id, { width: nw, height: nh, x: nx, y: ny })}
                onRemove={() => removeSticker('back', s.id)}
              />
            ))}
            <div className="cover-back-url" style={{ color: cover.style.urlColor || '#000000' }}>
              www.fletaime.com
            </div>
          </div>
        </div>

        {/* ── Shpina (mes kopertinës së pasme dhe të përparme) ──
            Ruhet realisht si "page 2" në ZIP-in e porosisë (shih
            buildCoverPages te OrderForm.js dhe rendered nga
            generatePhotoBookPages te server/orderController.js), mes
            faqes 1 (ballina) dhe faqes 3 (mbrapanja). */}
        <div className="cover-spine-wrap">
          {/* Hapësirë boshe që përputhet SAKTËSISHT (e matur, jo me sy) me
              lartësinë e rreshtit të bar-it të jashtëm mbi cover-page-wrap
              (gjithmonë e pranishme, e dukshme ose jo) — kështu shiriti i
              shpinës fillon pikërisht aty ku fillon vetë faqja, pa hendek. */}
          <div className="cover-spine-bar-spacer" style={{ height: spineTopOffset }} />
          <div
            className="cover-spine"
            style={{
              backgroundColor: cover.style.bg,
              height: spineHeight || undefined,
              width: `${Math.max(10, Math.round(18 * sf))}px`,
            }}
          >
            {cover.meta.spineTitle && (
              <span
                className="cover-spine-text cover-spine-text-title"
                style={{
                  color: cover.style.spineTextColor || '#5c2828',
                  fontFamily: `'${cover.style.fontFamily}', sans-serif`,
                  fontSize: `${Math.round(13 * sf)}px`,
                }}
              >
                {cover.meta.spineTitle}
              </span>
            )}
            {cover.meta.spineDate && (
              <span
                className="cover-spine-text cover-spine-text-date"
                style={{
                  color: cover.style.spineTextColor || '#5c2828',
                  fontFamily: `'${cover.style.fontFamily}', sans-serif`,
                  fontSize: `${Math.round(13 * sf)}px`,
                }}
              >
                {cover.meta.spineDate}
              </span>
            )}

            {cover.spineSticker ? (
              <button
                type="button"
                className="cover-spine-sticker"
                style={{ width: spineIconPx, height: spineIconPx }}
                onClick={() => update({ spineSticker: null })}
                title={t('cover.delete')}
              >
                <img src={cover.spineSticker.svgUrl} alt="" draggable={false} />
              </button>
            ) : (
              <button
                type="button"
                className="cover-spine-sticker-add"
                style={{ width: spineIconPx, height: spineIconPx }}
                onClick={() => { update({ activeStickerTarget: 'spine', _openStickersTab: Date.now() }); onOpenDrawer?.(); }}
                title={t('cover.stickersTitle')}
              >+</button>
            )}
          </div>
        </div>

        {/* ── Front cover ── */}
        <div className="cover-page-wrap">
          <div style={{ visibility: frontHasExternalBar ? 'visible' : 'hidden', pointerEvents: frontHasExternalBar ? 'auto' : 'none' }}>
            <SlotExternalBar
              slot={(cover.frontSlots || [])[0]}
              onUpdate={s => updateSlot('front', 0, s)}
              onRemove={() => removeSlot('front', 0)}
              onUpload={() => handleSlotClick('front', 0)}
            />
          </div>
          <div ref={frontPageRef} className="cover-page large cover-front" style={{ backgroundColor: cover.style.bg }}>
            <PhotoArea
              layout={cover.frontLayout}
              slots={cover.frontSlots || [null]}
              onSlotClick={i => handleSlotClick('front', i)}
              onSlotUpdate={(i, s) => updateSlot('front', i, s)}
              onSlotRemove={i => removeSlot('front', i)}
              noBar={frontHasExternalBar}
            />

            {cover.meta.title && (
              <DraggableText
                value={cover.meta.title}
                x={cover.style.titleX ?? 50}
                y={cover.style.titleY ?? 73}
                textStyle={titleStyle}
                onMove={(nx, ny) => updateStyle({ titleX: nx, titleY: ny })}
                onChange={v => updateMeta({ title: v })}
              />
            )}

            {cover.meta.year && (
              <DraggableText
                value={cover.meta.year}
                x={cover.style.yearX ?? 50}
                y={cover.style.yearY ?? 83}
                textStyle={yearStyle}
                onMove={(nx, ny) => updateStyle({ yearX: nx, yearY: ny })}
                onChange={v => updateMeta({ year: v })}
              />
            )}

            {(cover.frontStickers || []).map(s => (
              <CoverSticker
                key={s.id}
                sticker={s}
                onMove={(nx, ny) => updateSticker('front', s.id, { x: nx, y: ny })}
                onResize={(nw, nh, nx, ny) => updateSticker('front', s.id, { width: nw, height: nh, x: nx, y: ny })}
                onRemove={() => removeSticker('front', s.id)}
              />
            ))}

            <div className="cover-page-tag">{t('cover.front')}</div>
          </div>
        </div>
      </div>

      <p className="cover-preview-note">
          {/* {t('cover.previewNote')} */}
      </p>

      <input ref={slotFileRef} type="file" accept="image/*" hidden onChange={handleSlotFiles} />

      {showIntro && (
        <div className="cover-intro-overlay" onClick={dismissIntro}>
          <div className="cover-intro-card" onClick={e => e.stopPropagation()}>
            <h4>{t('cover.introTitle')}</h4>
            <p>{t('cover.introText')}</p>
            <button type="button" className="cover-intro-close" onClick={dismissIntro}>
              {t('cover.introClose')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// DEFAULT EXPORT: CoverEditor (full split — for standalone use)
// ══════════════════════════════════════════════════════════════════
const CoverEditor = ({ coverData, onUpdate }) => (
  <div className="cover-editor-v2">
    <CoverControls coverData={coverData} onUpdate={onUpdate} />
    <CoverPreview   coverData={coverData} onUpdate={onUpdate} />
  </div>
);

export default CoverEditor;
